import { Body1, makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { spacing, statusPalette } from '@/theme/tokens';
import type { ResourceHealth } from '@/api/types';

interface ResourceListProps {
  resources: readonly ResourceHealth[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
    paddingRight: spacing.s,
  },
  groupHeader: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: tokens.fontWeightSemibold,
    paddingLeft: spacing.s,
    paddingBottom: spacing.xxs,
  },
  groupItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
    padding: `${spacing.s} ${spacing.m}`,
    borderRadius: tokens.borderRadiusMedium,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
    width: '100%',
    transition: 'background-color 80ms ease, border-color 80ms ease',
    ':hover': { backgroundColor: tokens.colorNeutralBackground3Hover },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  itemSelected: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorBrandStroke2}`,
  },
  itemBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  itemName: {
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemSub: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dot: {
    flexShrink: 0,
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: statusPalette.unknown,
  },
  dotHealthy: { backgroundColor: statusPalette.healthy },
  dotDegraded: { backgroundColor: statusPalette.degraded },
  dotUnhealthy: { backgroundColor: statusPalette.unhealthy },
  empty: {
    paddingLeft: spacing.s,
    color: tokens.colorNeutralForeground3,
  },
});

const STATUS_DOT: Record<ResourceHealth['status'], string | undefined> = {
  Healthy: 'dotHealthy',
  Degraded: 'dotDegraded',
  Unhealthy: 'dotUnhealthy',
  Unknown: undefined,
};

export function ResourceList({ resources, selectedId, onSelect }: ResourceListProps) {
  const classes = useStyles();
  const grouped = useMemo(() => groupByKind(resources), [resources]);

  if (grouped.length === 0) {
    return (
      <Body1 className={classes.empty}>
        No resources visible for this environment yet — the dashboard catalog is empty.
      </Body1>
    );
  }

  return (
    <nav className={classes.list} aria-label="Resources">
      {grouped.map(({ kind, items }) => (
        <div key={kind}>
          <Text className={classes.groupHeader}>{prettyKind(kind)}</Text>
          <div className={classes.groupItems}>
            {items.map((r) => {
              const dotKey = STATUS_DOT[r.status];
              const dotClass = dotKey
                ? mergeClasses(
                    classes.dot,
                    dotKey === 'dotHealthy' ? classes.dotHealthy : undefined,
                    dotKey === 'dotDegraded' ? classes.dotDegraded : undefined,
                    dotKey === 'dotUnhealthy' ? classes.dotUnhealthy : undefined,
                  )
                : classes.dot;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    onSelect(r.id);
                  }}
                  className={mergeClasses(
                    classes.item,
                    selectedId === r.id ? classes.itemSelected : undefined,
                  )}
                  aria-current={selectedId === r.id ? 'true' : undefined}
                >
                  <span className={dotClass} aria-hidden="true" />
                  <span className={classes.itemBody}>
                    <span className={classes.itemName}>{r.name}</span>
                    <span className={classes.itemSub}>{r.region}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function groupByKind(resources: readonly ResourceHealth[]): { kind: string; items: ResourceHealth[] }[] {
  const map = new Map<string, ResourceHealth[]>();
  for (const r of resources) {
    const list = map.get(r.kind) ?? [];
    list.push(r);
    map.set(r.kind, list);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([kind, items]) => ({
      kind,
      items: items.sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

function prettyKind(kind: string): string {
  // Combine the provider's last dotted segment with the resource type for a
  // more recognizable group header — e.g.:
  //   Microsoft.Web/sites                                    → Web Sites
  //   Microsoft.DocumentDB/databaseAccounts                  → DocumentDB Database Accounts
  //   Microsoft.HealthcareApis/workspaces/fhirservices       → HealthcareApis Fhirservices
  // Fully unknown shapes fall back to the trailing segment only.
  const slashParts = kind.split('/');
  const trailing = slashParts.length > 1 ? slashParts[slashParts.length - 1] : kind;
  const providerSegments = slashParts[0]?.split('.') ?? [];
  const providerLeaf = providerSegments[providerSegments.length - 1];
  const head = slashParts.length > 1 && providerLeaf && providerLeaf !== 'Microsoft' ? providerLeaf : '';
  const combined = head ? `${head} ${trailing}` : trailing;
  return combined
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}
